import { useState } from 'react';

import styled from '@emotion/styled';
import { colors, fonts } from '@modernanimal/common';
import {
    searchTagSelectors
} from '@modernanimal/common/src/web/SmartBookingFlow/smartBookingFlow.helpers';
import { toLower } from 'lodash';
import { useBreakpoint } from 'use-breakpoint';

import PrimaryButton from '../components/PrimaryButton';
import SearchInputField from '../components/SearchInputField';
import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';
import Icons from '../icons';
import useWebBookingStore from '../store';

const REASONS_FOR_VISIT = [
    'Wellness Visit', 'Vaccines', 'Diarrhea', 'Eye Issues', 'Coughing', 'Lump/Bump', 'Skin Issues',
    'Allergies', 'Nail Trim', 'Urinary Issues', 'Ear Issues', 'Wound', 'Prescription', 'Vomiting',
    'Itchiness', 'Limping', 'Bloodwork'
];

type Props = {
    goToNextStep: ()=> void;
};

const ReasonForVisit = ({ goToNextStep }: Props) => {
    const { client, appointment, updateAppointment } = useWebBookingStore((state) => ({
        client: state.client,
        appointment: state.appointment,
        updateAppointment: state.setAppointment
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    const [selectedReasons, setSelectedReasons] = useState<string[]>(
        appointment.reasonForVisitDescription
            ? appointment.reasonForVisitDescription.split(', ')
            : ['Wellness Visit']
    );

    const searchReasonsForVisit = async (searchValue: string) => {
        if (!searchValue) return [];

        const tagSelectors = await searchTagSelectors(searchValue, client.token, true);
        const suggestionsToShow = tagSelectors.filter(
            (tagSelector) => !selectedReasons.includes(tagSelector.name)
        );

        return suggestionsToShow.map((suggestion) => toLower(suggestion.name)).includes(toLower(searchValue))
            ? suggestionsToShow
            : [{ id: '-1', name: searchValue }, ...suggestionsToShow];
    };

    const handleRfvPress = (rfv: string) => (
        setSelectedReasons((selectedReasons.includes(rfv)
            ? selectedReasons.filter((selectedRfv) => selectedRfv !== rfv)
            : [...selectedReasons, rfv]))
    );

    const handleNext = () => {
        updateAppointment({
            ...appointment,
            reasonForVisitDescription: selectedReasons.join(', ')
        });
        goToNextStep();
    };

    return (
        <ReasonForVisitContainer>
            <Body>
                <Fonts.Heading04>Reason for visit</Fonts.Heading04>
                <ReasonsList>
                    {REASONS_FOR_VISIT.map((rfv) => {
                        const isSelected = selectedReasons.includes(rfv);
                        return (
                            <ReasonButton
                                key={rfv}
                                type="button"
                                onClick={() => handleRfvPress(rfv)}
                                isSelected={isSelected}
                            >
                                <ReasonText isSelected={isSelected}>{rfv}</ReasonText>
                            </ReasonButton>
                        );
                    })}
                </ReasonsList>
                <MoreReasonsContainer>
                    <Fonts.LabelMedium>More reasons</Fonts.LabelMedium>
                    <SearchInputField
                        placeholder="Start typing to see more options"
                        selectedValue={null}
                        searchQuery={searchReasonsForVisit}
                        onSelectResult={(result: { id: string, name: string }) => handleRfvPress(result.name)}
                        searchResultKey="id"
                        searchResultName="name"
                        selectOnEnter
                        clearOnSelect
                    />
                    <ReasonsList>
                        {selectedReasons.filter((rfv) => !REASONS_FOR_VISIT.includes(rfv)).map((rfv) => (
                            <ReasonButton
                                key={rfv}
                                onClick={() => handleRfvPress(rfv)}
                                isSelected
                            >
                                <ReasonText isSelected>{rfv}</ReasonText>
                                <Icons.X width={16} height={16} fill={colors.PRIMARY_PERIWINKLE} />
                            </ReasonButton>
                        ))}
                    </ReasonsList>
                </MoreReasonsContainer>
            </Body>
            <NextButtonContainer isMobile={isMobile}>
                <PrimaryButton
                    id="reason-for-visit-button"
                    color="periwinkle"
                    onClick={handleNext}
                    disabled={!selectedReasons.length}
                >
                    Next
                </PrimaryButton>
            </NextButtonContainer>
        </ReasonForVisitContainer>
    );
};

export default ReasonForVisit;

const ReasonForVisitContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    padding: 32px 24px 0 24px;
    gap: 8px;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const MoreReasonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const NextButtonContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0px;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const ReasonsList = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
`;

const ReasonButton = styled.button<{ isSelected?: boolean }>`
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-radius: 20px;
    gap: 6px;
    background-color: ${({ isSelected }) => (isSelected ? colors.PERIWINKLE_4 : colors.GREY_7)};
    color: ${colors.GREY_1};
    border: 1px solid ${({ isSelected }) => (isSelected ? colors.PRIMARY_PERIWINKLE : colors.GREY_7)};
`;

const ReasonText = styled(Fonts.BodySmall) <{ isSelected: boolean }>`
    ${({ isSelected }) => (isSelected ? `
        font-family: ${fonts.baseMedium.fontFamily};
        color: ${colors.PRIMARY_PERIWINKLE};
    ` : `
        padding: 0 .5px;
    `)};
`;
